import { storageKey, createScopedStorage, readLocalJson, writeLocalJson, removeLocalKey } from '@authe/fe-commons/storage';
import { EventResourceType, resourceLabel } from '@authe/fe-commons/events';
export { EventResourceType } from '@authe/fe-commons/events';
import { createLogger } from '@authe/fe-commons/logging';
import { unwrapData } from '@authe/fe-commons/api';
import { TagList, ChainAvatar, RiskBadge, EntityCard, AddressDisplay, HashDisplay, Badge, cn, HoverCard, HoverCardTrigger, HoverCardContent, CommandGroup, CommandItem, Dialog, DialogContent, DialogTitle, DialogDescription, Command, CommandInput, CommandList, Skeleton, CommandEmpty, CommandSeparator, Popover, PopoverTrigger, Button, PopoverContent, AttributionChips } from '@authe/ui-core';
import { riskLevelFromScore } from '@authe/fe-commons/precedence';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React2 from 'react';

// src/config.ts
var SEARCH_PACKAGE = "search";
var SEARCH_RECENTS_KEY = storageKey(SEARCH_PACKAGE, "recents");
var SEARCH_RECENTS_NAME = `${SEARCH_PACKAGE}:recents`;
var SEARCH_DEBOUNCE_MS = 200;
var SEARCH_MIN_QUERY_LENGTH = 2;
var SEARCH_RECENTS_LIMIT = 8;
var SEARCH_DEFAULT_LIMIT = 20;
var SEARCH_TENANT_KIND = "tenant";
var SEARCH_RESOURCE_KINDS = [
  EventResourceType.TRANSACTION,
  EventResourceType.ADDRESS,
  EventResourceType.ENTITY,
  EventResourceType.LABEL,
  EventResourceType.TAG,
  EventResourceType.GROUP,
  EventResourceType.USER,
  SEARCH_TENANT_KIND,
  EventResourceType.ORGANIZATION
];
function resourceKindLabel(kind) {
  if (kind === SEARCH_TENANT_KIND) return "Tenant";
  return resourceLabel(kind);
}
function resourceKindOrder(kind) {
  const idx = SEARCH_RESOURCE_KINDS.indexOf(kind);
  return idx === -1 ? SEARCH_RESOURCE_KINDS.length : idx;
}

// src/model/result.ts
function hitChains(hit) {
  const chains = hit.enrichment?.chains;
  if (chains && chains.length) return chains;
  return hit.chainId ? [hit.chainId] : [];
}
function chainScopedHit(hit, chainId) {
  return { ...hit, chainId, enrichment: { ...hit.enrichment, chains: [chainId] } };
}
function groupHitsByKind(hits) {
  const byKind = /* @__PURE__ */ new Map();
  for (const hit of hits) {
    const bucket = byKind.get(hit.kind);
    if (bucket) bucket.push(hit);
    else byKind.set(hit.kind, [hit]);
  }
  return [...byKind.entries()].sort((a, b) => resourceKindOrder(a[0]) - resourceKindOrder(b[0])).map(([kind, kindHits]) => ({ kind, label: resourceKindLabel(kind), hits: kindHits }));
}
function byRelevance(a, b) {
  const sa = a.score ?? 0;
  const sb = b.score ?? 0;
  if (sa !== sb) return sb - sa;
  return a.title.localeCompare(b.title);
}
var log = createLogger(`${SEARCH_PACKAGE}:provider`);
function servesAnyKind(provider, kinds) {
  if (!provider.kinds || !kinds || kinds.length === 0) return true;
  return kinds.some((k) => provider.kinds.includes(k));
}
function mergeFacets(facets) {
  const byField = /* @__PURE__ */ new Map();
  for (const facet of facets) {
    const existing = byField.get(facet.field);
    if (!existing) {
      byField.set(facet.field, { ...facet, values: facet.values.map((v) => ({ ...v })) });
      continue;
    }
    for (const value of facet.values) {
      const match = existing.values.find((v) => v.value === value.value);
      if (match) match.count = (match.count ?? 0) + (value.count ?? 0);
      else existing.values.push({ ...value });
    }
  }
  return [...byField.values()];
}
function createCompositeSearchProvider(providers, options = {}) {
  const id = options.id ?? "composite";
  return {
    id,
    query: async (query, ctx) => {
      const targets = providers.filter((p) => servesAnyKind(p, query.kinds));
      const settled = await Promise.allSettled(targets.map((p) => p.query(query, ctx)));
      const hits = [];
      const seen = /* @__PURE__ */ new Set();
      const facets = [];
      let total = 0;
      let sawTotal = false;
      settled.forEach((result, i) => {
        if (result.status === "rejected") {
          log.warn("search provider failed", { provider: targets[i]?.id, error: String(result.reason) });
          return;
        }
        const res = result.value;
        for (const hit of res.hits) {
          const dedupeKey = `${hit.kind}:${hit.id}`;
          if (seen.has(dedupeKey)) continue;
          seen.add(dedupeKey);
          hits.push(hit);
        }
        if (res.facets) facets.push(...res.facets);
        if (typeof res.total === "number") {
          total += res.total;
          sawTotal = true;
        }
      });
      hits.sort(byRelevance);
      const limit = query.limit ?? SEARCH_DEFAULT_LIMIT;
      return {
        hits: hits.slice(0, limit),
        facets: facets.length ? mergeFacets(facets) : void 0,
        total: sawTotal ? total : null
      };
    },
    suggest: async (text, ctx) => {
      const withSuggest = providers.filter((p) => typeof p.suggest === "function");
      const settled = await Promise.allSettled(withSuggest.map((p) => p.suggest(text, ctx)));
      const out = /* @__PURE__ */ new Set();
      for (const result of settled) {
        if (result.status === "fulfilled") for (const s of result.value) out.add(s);
      }
      return [...out];
    }
  };
}
function createInMemorySearchProvider(seed, options = {}) {
  const id = options.id ?? "in-memory";
  return {
    id,
    query: async (query) => {
      const text = query.text.trim().toLowerCase();
      const kinds = query.kinds && query.kinds.length ? new Set(query.kinds) : null;
      let matches = seed.filter((hit) => {
        if (kinds && !kinds.has(hit.kind)) return false;
        if (query.chainId && hit.chainId && hit.chainId !== query.chainId) return false;
        if (!text) return true;
        return hit.id.toLowerCase().includes(text) || hit.title.toLowerCase().includes(text) || (hit.subtitle?.toLowerCase().includes(text) ?? false);
      });
      matches = matches.sort(byRelevance);
      const counts = /* @__PURE__ */ new Map();
      for (const hit of matches) counts.set(hit.kind, (counts.get(hit.kind) ?? 0) + 1);
      const kindFacet = {
        field: "kind",
        label: "Type",
        values: SEARCH_RESOURCE_KINDS.filter((k) => counts.has(k)).map((k) => ({
          value: k,
          label: resourceKindLabel(k),
          count: counts.get(k)
        }))
      };
      const limit = query.limit ?? SEARCH_DEFAULT_LIMIT;
      return { hits: matches.slice(0, limit), facets: [kindFacet], total: matches.length };
    },
    suggest: async (text) => {
      const t = text.trim().toLowerCase();
      if (!t) return [];
      const out = /* @__PURE__ */ new Set();
      for (const hit of seed) {
        if (hit.title.toLowerCase().includes(t)) out.add(hit.title);
        if (out.size >= 8) break;
      }
      return [...out];
    }
  };
}

// src/provider/wire.ts
var asRecord = (value) => value && typeof value === "object" ? value : {};
var asArray = (value) => Array.isArray(value) ? value : [];
var asString = (value) => typeof value === "string" && value ? value : void 0;
var asNumber = (value) => typeof value === "number" && Number.isFinite(value) ? value : void 0;
function toSearchRequestBody(query) {
  return {
    text: query.text,
    ...query.kinds && query.kinds.length ? { kinds: query.kinds } : {},
    ...query.chainId ? { chain_id: query.chainId } : {},
    ...typeof query.limit === "number" ? { limit: query.limit } : {},
    ...query.cursor ? { cursor: query.cursor } : {}
  };
}
function mapLabel(raw) {
  return {
    label: asString(raw.label) ?? "",
    value: asString(raw.value),
    scope: asString(raw.scope)
  };
}
function mapTag(raw) {
  return {
    label: asString(raw.label) ?? "",
    severity: asString(raw.severity)
  };
}
function mapEntity(raw) {
  return {
    id: asString(raw.id) ?? "",
    name: asString(raw.name) ?? "",
    entityType: asString(raw.entity_type),
    avatarUrl: asString(raw.avatar_url)
  };
}
function mapGroup(raw) {
  return {
    id: asString(raw.id) ?? "",
    name: asString(raw.name),
    count: asNumber(raw.count)
  };
}
function mapEnrichment(value) {
  if (!value || typeof value !== "object") return void 0;
  const raw = value;
  return {
    name: asString(raw.name),
    chains: Array.isArray(raw.chains) ? raw.chains.filter((c) => typeof c === "string") : void 0,
    labels: asArray(raw.labels).map(mapLabel),
    tags: asArray(raw.tags).map(mapTag),
    entity: raw.entity ? mapEntity(asRecord(raw.entity)) : null,
    groups: asArray(raw.groups).map(mapGroup),
    riskLevel: asString(raw.risk_level),
    riskScore: asNumber(raw.risk_score),
    logoUrl: asString(raw.logo_url),
    extra: raw.extra && typeof raw.extra === "object" ? raw.extra : void 0
  };
}
function mapHit(raw) {
  return {
    id: asString(raw.id) ?? "",
    kind: asString(raw.kind) ?? "",
    title: asString(raw.title) ?? "",
    subtitle: asString(raw.subtitle),
    chainId: asString(raw.chain_id) ?? null,
    score: asNumber(raw.score),
    href: asString(raw.href),
    enrichment: mapEnrichment(raw.enrichment),
    metadata: raw.metadata && typeof raw.metadata === "object" ? raw.metadata : void 0
  };
}
function mapFacet(raw) {
  return {
    field: asString(raw.field) ?? "",
    label: asString(raw.label) ?? "",
    values: asArray(raw.values).map((v) => ({
      value: asString(v.value) ?? "",
      label: asString(v.label) ?? "",
      count: asNumber(v.count)
    }))
  };
}
function fromSearchResponseDto(dto) {
  const raw = asRecord(dto);
  return {
    hits: asArray(raw.hits).map(mapHit),
    facets: asArray(raw.facets).map(mapFacet),
    nextCursor: asString(raw.next_cursor) ?? null,
    total: asNumber(raw.total) ?? null
  };
}

// src/provider/apiSearchProvider.ts
function createApiSearchProvider(client, options = {}) {
  const id = options.id ?? "api";
  const path = options.path ?? "/search";
  return {
    id,
    kinds: options.kinds,
    async query(query, ctx) {
      const dto = unwrapData(
        await client.post(path, toSearchRequestBody(query), {
          signal: ctx?.signal,
          cacheTtlMs: 0
        })
      );
      return fromSearchResponseDto(dto);
    },
    ...options.suggestPath ? {
      async suggest(text, ctx) {
        return unwrapData(
          await client.get(options.suggestPath, {
            query: { q: text },
            signal: ctx?.signal
          })
        );
      }
    } : {}
  };
}
var SEARCH_PREVIEW_FIELDS = [
  "chain",
  "risk",
  "entity",
  "labels",
  "tags",
  "groups",
  "name"
];
var DEFAULT_SEARCH_PREVIEW_CONFIG = {
  density: "compact",
  hover: true,
  defaultFields: [
    { field: "chain" },
    { field: "labels", max: 2 },
    { field: "tags", max: 2, detailOnly: true }
  ],
  fieldsByKind: {
    [EventResourceType.TRANSACTION]: [
      { field: "chain" },
      { field: "risk" },
      { field: "entity" },
      { field: "labels", max: 2 },
      { field: "tags", max: 3, detailOnly: true },
      { field: "groups", max: 2, detailOnly: true }
    ],
    [EventResourceType.ADDRESS]: [
      { field: "chain" },
      { field: "risk" },
      { field: "entity" },
      { field: "labels", max: 3 },
      { field: "tags", max: 3, detailOnly: true },
      { field: "groups", max: 2, detailOnly: true }
    ],
    [EventResourceType.ENTITY]: [
      { field: "labels", max: 3 },
      { field: "tags", max: 3, detailOnly: true }
    ],
    [EventResourceType.GROUP]: [
      { field: "labels", max: 2 },
      { field: "tags", max: 2, detailOnly: true }
    ],
    // Management resources have no chain/catalog context — just the display
    // name (the hit row already shows the title + a slug/email subtitle).
    [EventResourceType.USER]: [{ field: "name" }],
    [EventResourceType.ORGANIZATION]: [{ field: "name" }],
    [SEARCH_TENANT_KIND]: [{ field: "name" }]
  }
};
function resolveFields(config, kind) {
  return config.fieldsByKind[kind] ?? config.defaultFields;
}
var SEARCH_PREVIEW_FIELD_LABELS = {
  chain: "Chain",
  risk: "Risk",
  entity: "Entity",
  labels: "Labels",
  tags: "Tags",
  groups: "Groups",
  name: "Name"
};
function previewFieldLabel(field) {
  return SEARCH_PREVIEW_FIELD_LABELS[field] ?? field.replace(/^\w/, (c) => c.toUpperCase());
}
function attributionRenderer(kind, bundleFor, defaultMax) {
  return ({ hit, field }) => /* @__PURE__ */ jsx(
    AttributionChips,
    {
      bundle: bundleFor(hit),
      kinds: [kind],
      max: field.max ?? defaultMax,
      popoverTitle: kind === "entity" ? "Entities" : `${kind[0].toUpperCase()}${kind.slice(1)}s`
    }
  );
}
var renderChain = ({ hit, field }) => {
  const chains = hitChains(hit);
  if (!chains.length) return null;
  return /* @__PURE__ */ jsx(
    TagList,
    {
      max: field.max ?? 2,
      popoverTitle: "Chains",
      items: chains.map((c) => /* @__PURE__ */ jsx(ChainAvatar, { chain: c, size: "sm", showName: false }, c))
    }
  );
};
var renderRisk = ({ hit }) => {
  const e = hit.enrichment;
  if (!e) return null;
  const level = e.riskLevel ?? riskLevelFromScore(e.riskScore);
  if (!level) return null;
  return /* @__PURE__ */ jsx(RiskBadge, { level, score: e.riskScore });
};
var renderName = ({ hit }) => {
  const name = hit.enrichment?.name;
  if (!name) return null;
  return /* @__PURE__ */ jsx("span", { className: "truncate text-xs text-muted-foreground", children: name });
};
var renderEntity = attributionRenderer(
  "entity",
  (hit) => {
    const entity = hit.enrichment?.entity;
    if (!entity) return {};
    return {
      entities: [
        {
          id: entity.id,
          name: entity.name,
          entityType: entity.entityType,
          logo: entity.avatarUrl
        }
      ]
    };
  },
  1
);
var renderLabels = attributionRenderer(
  "label",
  (hit) => ({
    labels: (hit.enrichment?.labels ?? []).map((l, i) => ({
      id: `${l.label}-${i}`,
      name: l.label,
      value: l.value,
      scope: l.scope
    }))
  }),
  3
);
var renderTags = attributionRenderer(
  "tag",
  (hit) => ({
    tags: (hit.enrichment?.tags ?? []).map((t, i) => ({
      id: `${t.label}-${i}`,
      name: t.label,
      severity: t.severity
    }))
  }),
  3
);
var renderGroups = attributionRenderer(
  "group",
  (hit) => ({
    groups: (hit.enrichment?.groups ?? []).map((g) => ({
      id: g.id,
      name: g.name ?? g.id,
      count: g.count
    }))
  }),
  2
);
var DEFAULT_FIELD_RENDERERS = {
  chain: renderChain,
  risk: renderRisk,
  entity: renderEntity,
  name: renderName,
  labels: renderLabels,
  tags: renderTags,
  groups: renderGroups
};
var FALLBACK = {
  config: DEFAULT_SEARCH_PREVIEW_CONFIG,
  renderers: DEFAULT_FIELD_RENDERERS
};
var SearchPreviewContext = React2.createContext(null);
function SearchPreviewProvider({
  config,
  renderers,
  children
}) {
  const value = React2.useMemo(
    () => ({
      config: config ?? DEFAULT_SEARCH_PREVIEW_CONFIG,
      renderers: renderers ? { ...DEFAULT_FIELD_RENDERERS, ...renderers } : DEFAULT_FIELD_RENDERERS
    }),
    [config, renderers]
  );
  return /* @__PURE__ */ jsx(SearchPreviewContext.Provider, { value, children });
}
function useSearchPreview() {
  return React2.useContext(SearchPreviewContext) ?? FALLBACK;
}
var log2 = createLogger(`${SEARCH_PACKAGE}:use-search`);
function useSearch(options) {
  const {
    provider,
    debounceMs = SEARCH_DEBOUNCE_MS,
    minLength = SEARCH_MIN_QUERY_LENGTH,
    initialText = "",
    initialKinds = [],
    chainId = null,
    limit = SEARCH_DEFAULT_LIMIT,
    autoSearch = true
  } = options;
  const [text, setText] = React2.useState(initialText);
  const [kinds, setKinds] = React2.useState(initialKinds);
  const [status, setStatus] = React2.useState("idle");
  const [hits, setHits] = React2.useState([]);
  const [facets, setFacets] = React2.useState([]);
  const [total, setTotal] = React2.useState(null);
  const [error, setError] = React2.useState(null);
  const abortRef = React2.useRef(null);
  const runRef = React2.useRef(0);
  const run = React2.useCallback(
    async (query) => {
      const q = query.text.trim();
      abortRef.current?.abort();
      if (q.length < minLength) {
        setStatus("idle");
        setHits([]);
        setFacets([]);
        setTotal(null);
        setError(null);
        return;
      }
      const controller = new AbortController();
      abortRef.current = controller;
      const runId = ++runRef.current;
      setStatus("searching");
      setError(null);
      try {
        const res = await provider.query(query, { signal: controller.signal });
        if (runId !== runRef.current || controller.signal.aborted) return;
        setHits(res.hits);
        setFacets(res.facets ?? []);
        setTotal(res.total ?? null);
        setStatus("ready");
      } catch (err) {
        if (controller.signal.aborted || runId !== runRef.current) return;
        log2.warn("search failed", { text: q, error: String(err) });
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus("error");
      }
    },
    [provider, minLength]
  );
  React2.useEffect(() => {
    if (!autoSearch) return void 0;
    const handle = setTimeout(() => {
      void run({ text, kinds, chainId, limit });
    }, debounceMs);
    return () => clearTimeout(handle);
  }, [text, kinds, chainId, limit, autoSearch, debounceMs, run]);
  React2.useEffect(() => () => abortRef.current?.abort(), []);
  const toggleKind = React2.useCallback((kind) => {
    setKinds((prev) => prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind]);
  }, []);
  const search = React2.useCallback(
    (override) => {
      void run({ text, kinds, chainId, limit, ...override });
    },
    [run, text, kinds, chainId, limit]
  );
  const clear = React2.useCallback(() => {
    abortRef.current?.abort();
    setText("");
    setHits([]);
    setFacets([]);
    setTotal(null);
    setError(null);
    setStatus("idle");
  }, []);
  const groups = React2.useMemo(() => groupHitsByKind(hits), [hits]);
  return {
    text,
    setText,
    kinds,
    setKinds,
    toggleKind,
    status,
    hits,
    groups,
    facets,
    total,
    error,
    search,
    clear
  };
}
function useRecentSearches(options = {}) {
  const { identity, limit = SEARCH_RECENTS_LIMIT } = options;
  const identityKey = identity ? [identity.app, identity.customerId, identity.orgId, identity.tenantId, identity.userId].join("|") : "";
  const read = React2.useCallback(() => {
    if (identity) return createScopedStorage(identity).get(SEARCH_RECENTS_NAME, []);
    return readLocalJson(SEARCH_RECENTS_KEY, []);
  }, [identityKey]);
  const write = React2.useCallback(
    (next) => {
      if (identity) createScopedStorage(identity).set(SEARCH_RECENTS_NAME, next);
      else writeLocalJson(SEARCH_RECENTS_KEY, next);
    },
    [identityKey]
  );
  const [recents, setRecents] = React2.useState(() => read());
  React2.useEffect(() => {
    setRecents(read());
  }, [read]);
  const add = React2.useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setRecents((prev) => {
        const without = prev.filter((r) => r.text.toLowerCase() !== trimmed.toLowerCase());
        const next = [{ text: trimmed, at: Date.now() }, ...without].slice(0, limit);
        write(next);
        return next;
      });
    },
    [write, limit]
  );
  const remove = React2.useCallback(
    (text) => {
      setRecents((prev) => {
        const next = prev.filter((r) => r.text.toLowerCase() !== text.toLowerCase());
        write(next);
        return next;
      });
    },
    [write]
  );
  const clear = React2.useCallback(() => {
    if (identity) createScopedStorage(identity).remove(SEARCH_RECENTS_NAME);
    else removeLocalKey(SEARCH_RECENTS_KEY);
    setRecents([]);
  }, [identityKey]);
  return { recents, add, remove, clear };
}
function SearchHitDetails({ hit, config, renderers }) {
  const e = hit.enrichment;
  if (hit.kind === EventResourceType.ENTITY) {
    return /* @__PURE__ */ jsx(
      EntityCard,
      {
        name: e?.name ?? hit.title,
        entityType: e?.entity?.entityType,
        avatarUrl: e?.logoUrl ?? e?.entity?.avatarUrl,
        description: hit.subtitle,
        tags: (e?.tags ?? []).map((t) => t.label)
      }
    );
  }
  const primaryChain = e?.chains?.[0] ?? hit.chainId ?? void 0;
  const fields = resolveFields(config, hit.kind);
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx("span", { className: "text-[11px] font-medium uppercase tracking-wide text-muted-foreground", children: resourceKindLabel(hit.kind) }),
    hit.kind === EventResourceType.ADDRESS ? /* @__PURE__ */ jsx(AddressDisplay, { address: hit.title, chain: primaryChain, label: e?.name, showExplorerIcon: true }) : hit.kind === EventResourceType.TRANSACTION ? /* @__PURE__ */ jsx(HashDisplay, { hash: hit.title, chain: primaryChain, showExplorerIcon: true }) : /* @__PURE__ */ jsx("div", { className: "text-sm font-medium", children: e?.name ?? hit.title }),
    hit.subtitle && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: hit.subtitle }),
    /* @__PURE__ */ jsx("div", { className: "space-y-1.5", children: fields.map((f) => {
      const node = renderers[f.field]?.({ hit, field: f, density: "detailed" });
      if (!node) return null;
      return /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "w-14 shrink-0 pt-0.5 text-[11px] uppercase tracking-wide text-muted-foreground", children: previewFieldLabel(f.field) }),
        /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: node })
      ] }, f.field);
    }) })
  ] });
}
var MONO_KINDS = /* @__PURE__ */ new Set([EventResourceType.TRANSACTION, EventResourceType.ADDRESS]);
function SearchHitItem({ hit, config: configProp, className }) {
  const ctx = useSearchPreview();
  const config = configProp ?? ctx.config;
  const renderers = ctx.renderers;
  const mono = MONO_KINDS.has(hit.kind);
  const fields = resolveFields(config, hit.kind);
  const inlineFields = config.density === "detailed" ? fields : fields.filter((f) => !f.detailOnly);
  const previews = inlineFields.map((f) => {
    const node = renderers[f.field]?.({ hit, field: f, density: config.density });
    return node ? /* @__PURE__ */ jsx(React2.Fragment, { children: node }, f.field) : null;
  }).filter(Boolean);
  const row = /* @__PURE__ */ jsxs("div", { className: cn("flex w-full items-center gap-2", className), children: [
    /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "shrink-0", children: resourceKindLabel(hit.kind) }),
    /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col", children: [
      /* @__PURE__ */ jsx("span", { className: cn("truncate", mono && "font-mono text-xs"), children: hit.title }),
      hit.subtitle && /* @__PURE__ */ jsx("span", { className: "truncate text-xs text-muted-foreground", children: hit.subtitle })
    ] }),
    previews.length > 0 && /* @__PURE__ */ jsx("span", { className: "ml-auto flex shrink-0 items-center gap-1.5", children: previews })
  ] });
  if (config.hover === false || !hit.enrichment) return row;
  return /* @__PURE__ */ jsxs(HoverCard, { openDelay: 140, closeDelay: 60, children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild: true, children: row }),
    /* @__PURE__ */ jsx(HoverCardContent, { align: "start", className: "w-80", children: /* @__PURE__ */ jsx(SearchHitDetails, { hit, config, renderers }) })
  ] });
}
function FacetFilters({
  selected,
  onToggle,
  kinds = SEARCH_RESOURCE_KINDS,
  counts,
  className
}) {
  const active = new Set(selected);
  return /* @__PURE__ */ jsx("div", { className: cn("flex flex-wrap items-center gap-1.5 border-b px-3 py-2", className), children: kinds.map((kind) => {
    const on = active.has(kind);
    const count = counts?.[kind];
    return /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        onClick: () => onToggle(kind),
        "aria-pressed": on,
        className: "appearance-none",
        children: /* @__PURE__ */ jsxs(
          Badge,
          {
            variant: on ? "default" : "outline",
            className: cn("cursor-pointer", !on && "text-muted-foreground"),
            children: [
              resourceKindLabel(kind),
              typeof count === "number" && /* @__PURE__ */ jsx("span", { className: "ml-1 opacity-70", children: count })
            ]
          }
        )
      },
      kind
    );
  }) });
}
function SearchResults({
  groups,
  onSelect,
  itemConfig,
  chainDrilldown = false
}) {
  const [expanded, setExpanded] = React2.useState(() => /* @__PURE__ */ new Set());
  const toggle = React2.useCallback((key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);
  return /* @__PURE__ */ jsx(Fragment, { children: groups.map((group) => /* @__PURE__ */ jsx(CommandGroup, { heading: `${group.label} \xB7 ${group.hits.length}`, children: group.hits.map((hit) => {
    const key = `${hit.kind}:${hit.id}`;
    const chains = chainDrilldown ? hitChains(hit) : [];
    const open = expanded.has(key);
    return /* @__PURE__ */ jsxs(React2.Fragment, { children: [
      /* @__PURE__ */ jsx(CommandItem, { value: key, onSelect: () => onSelect(hit), children: /* @__PURE__ */ jsx(SearchHitItem, { hit, config: itemConfig }) }),
      chains.length > 1 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(
          CommandItem,
          {
            value: `${key}:chains`,
            onSelect: () => toggle(key),
            className: "pl-8 text-xs text-muted-foreground",
            children: [
              /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: open ? "\u25BE" : "\u25B8" }),
              open ? "Hide chains" : `Open on a specific chain (${chains.length})`
            ]
          }
        ),
        open && chains.map((chain) => /* @__PURE__ */ jsx(
          CommandItem,
          {
            value: `${key}:${chain}`,
            onSelect: () => onSelect(chainScopedHit(hit, chain)),
            className: "pl-12 text-xs",
            children: /* @__PURE__ */ jsx(ChainAvatar, { chain, size: "sm" })
          },
          chain
        ))
      ] })
    ] }, key);
  }) }, group.kind)) });
}
function SearchCommand({
  provider,
  open,
  onOpenChange,
  onSelectHit,
  identity,
  chainId,
  showFacets = true,
  chainDrilldown = true,
  placeholder = "Search addresses, transactions, entities\u2026",
  emptyMessage = "No matches.",
  label = "Search"
}) {
  const search = useSearch({ provider, chainId });
  const recent = useRecentSearches({ identity });
  const kindCounts = React2.useMemo(() => {
    const facet = search.facets.find((f) => f.field === "kind");
    if (!facet) return void 0;
    const out = {};
    for (const v of facet.values) out[v.value] = v.count;
    return out;
  }, [search.facets]);
  const handleSelect = React2.useCallback(
    (hit) => {
      recent.add(search.text);
      onOpenChange(false);
      onSelectHit(hit);
    },
    [recent, search.text, onOpenChange, onSelectHit]
  );
  const showRecents = search.text.trim().length < SEARCH_MIN_QUERY_LENGTH;
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(DialogContent, { className: "max-w-2xl overflow-hidden p-0", showClose: false, children: [
    /* @__PURE__ */ jsx(DialogTitle, { className: "sr-only", children: label }),
    /* @__PURE__ */ jsx(DialogDescription, { className: "sr-only", children: placeholder }),
    /* @__PURE__ */ jsxs(Command, { shouldFilter: false, className: "[&_[cmdk-group-heading]]:px-2", children: [
      /* @__PURE__ */ jsx(CommandInput, { value: search.text, onValueChange: search.setText, placeholder }),
      showFacets && /* @__PURE__ */ jsx(FacetFilters, { selected: search.kinds, onToggle: search.toggleKind, counts: kindCounts }),
      /* @__PURE__ */ jsxs(CommandList, { className: "max-h-[60vh]", children: [
        search.status === "searching" && /* @__PURE__ */ jsxs("div", { className: "space-y-2 p-3", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-full" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-4/5" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-3/5" })
        ] }),
        search.status === "error" && /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-sm text-destructive", children: "Search failed. Try again." }),
        showRecents && search.status !== "searching" && recent.recents.length === 0 && /* @__PURE__ */ jsx("div", { className: "p-4 text-center text-sm text-muted-foreground", children: "Type to search across addresses, transactions, and entities." }),
        showRecents && recent.recents.length > 0 && /* @__PURE__ */ jsx(CommandGroup, { heading: "Recent", children: recent.recents.map((r) => /* @__PURE__ */ jsx(CommandItem, { value: `recent:${r.text}`, onSelect: () => search.setText(r.text), children: /* @__PURE__ */ jsx("span", { className: "truncate", children: r.text }) }, r.text)) }),
        !showRecents && search.status === "ready" && search.groups.length === 0 && /* @__PURE__ */ jsx(CommandEmpty, { children: emptyMessage }),
        !showRecents && search.groups.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          recent.recents.length > 0 && /* @__PURE__ */ jsx(CommandSeparator, {}),
          /* @__PURE__ */ jsx(
            SearchResults,
            {
              groups: search.groups,
              onSelect: handleSelect,
              chainDrilldown
            }
          )
        ] })
      ] })
    ] })
  ] }) });
}
var COMBOBOX_PREVIEW = {
  ...DEFAULT_SEARCH_PREVIEW_CONFIG,
  density: "compact",
  hover: false
};
function SearchCombobox({
  provider,
  kinds,
  chainId,
  value,
  onSelectHit,
  placeholder = "Select\u2026",
  searchPlaceholder = "Search\u2026",
  emptyMessage = "No matches.",
  disabled,
  className,
  contentClassName
}) {
  const [open, setOpen] = React2.useState(false);
  const search = useSearch({ provider, initialKinds: kinds, chainId });
  const handleSelect = React2.useCallback(
    (hit) => {
      setOpen(false);
      onSelectHit(hit);
    },
    [onSelectHit]
  );
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
      Button,
      {
        type: "button",
        variant: "outline",
        role: "combobox",
        "aria-expanded": open,
        disabled,
        className: cn("justify-between font-normal", className),
        children: [
          /* @__PURE__ */ jsx("span", { className: cn("truncate", !value && "text-muted-foreground"), children: value ? value.title : placeholder }),
          /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "ml-2 opacity-50", children: "\u2304" })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx(PopoverContent, { align: "start", className: cn("w-[var(--radix-popover-trigger-width)] p-0", contentClassName), children: /* @__PURE__ */ jsxs(Command, { shouldFilter: false, children: [
      /* @__PURE__ */ jsx(CommandInput, { value: search.text, onValueChange: search.setText, placeholder: searchPlaceholder }),
      /* @__PURE__ */ jsxs(CommandList, { children: [
        search.status === "searching" && /* @__PURE__ */ jsxs("div", { className: "space-y-2 p-2", children: [
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-full" }),
          /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-3/4" })
        ] }),
        search.status === "ready" && search.groups.length === 0 && /* @__PURE__ */ jsx(CommandEmpty, { children: emptyMessage }),
        search.groups.length > 0 && /* @__PURE__ */ jsx(SearchResults, { groups: search.groups, onSelect: handleSelect, itemConfig: COMBOBOX_PREVIEW })
      ] })
    ] }) })
  ] });
}

export { DEFAULT_FIELD_RENDERERS, DEFAULT_SEARCH_PREVIEW_CONFIG, FacetFilters, SEARCH_DEBOUNCE_MS, SEARCH_DEFAULT_LIMIT, SEARCH_MIN_QUERY_LENGTH, SEARCH_PACKAGE, SEARCH_PREVIEW_FIELDS, SEARCH_PREVIEW_FIELD_LABELS, SEARCH_RECENTS_KEY, SEARCH_RECENTS_LIMIT, SEARCH_RECENTS_NAME, SEARCH_RESOURCE_KINDS, SEARCH_TENANT_KIND, SearchCombobox, SearchCommand, SearchHitDetails, SearchHitItem, SearchPreviewProvider, SearchResults, byRelevance, chainScopedHit, createApiSearchProvider, createCompositeSearchProvider, createInMemorySearchProvider, groupHitsByKind, hitChains, previewFieldLabel, resolveFields, resourceKindLabel, resourceKindOrder, useRecentSearches, useSearch, useSearchPreview };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map